import { groupForSpelling } from './modelIndex';

/**
 * Slug ↔ provider value mapping for the /aukcje/[marka]/[model] routes
 * (spec 006, FR-027).
 *
 * WHY THIS IS A LOOKUP AND NOT A STRING TRANSFORM:
 * slugifying is lossy, so de-slugging cannot be its inverse. The provider's real
 * model names include "MX-5 MIATA", "F-350", "C 400 GT", "1500 CLASSIC" and
 * "S/SV/SL/PLATINUM" — every one of those collapses hyphens, spaces and slashes
 * into the same separator. Turning "mx-5-miata" back into a name by replacing
 * hyphens with spaces yields "MX 5 MIATA", which is not a value the provider
 * knows, and `make`/`model` are filters the API silently ignores when they match
 * nothing — the page would render as unfiltered inventory under a Mazda MX-5
 * heading.
 *
 * So a slug is only ever resolved by matching it against the real catalogue,
 * which is cached for 24h and already fetched for the autocomplete.
 */

/**
 * Lowercase, ASCII, hyphen-separated. Polish marques are unaffected but the
 * transform is diacritic-safe anyway, and every run of non-alphanumerics becomes
 * a single hyphen so "S/SV/SL" and "MX-5 MIATA" both produce stable slugs.
 */
export const toAuctionSlug = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/* -------------------------------------------------------------------------- */
/* Lot slugs                                                                  */
/* -------------------------------------------------------------------------- */

const MAX_LOT_SLUG_SEGMENT = 60;

/**
 * Readable lot URL: `chevrolet-express-g4500-2017-calgary-south-alberta-12598781`.
 *
 * The trailing lot number is the ONLY authoritative part. Everything before it is
 * decoration for humans — a lot's location and description can change between
 * revalidations, and a link someone pasted last week must not break because the
 * yard was renamed. `parseLotSlug` therefore reads the id and ignores the prefix,
 * and the page redirects to the canonical spelling so the two never compete as
 * separate URLs.
 */
export type TLotSlugInput = {
  make: string;
  model: string;
  year: number | null;
  location: string | null;
  lotNumber: number;
};

export const buildLotSlug = (lot: TLotSlugInput): string => {
  const description = [lot.make, lot.model, lot.year, lot.location]
    .filter(Boolean)
    .map(part => toAuctionSlug(String(part)))
    .filter(Boolean)
    .join('-')
    .slice(0, MAX_LOT_SLUG_SEGMENT)
    // A truncation can leave a trailing hyphen or a severed word's hyphen.
    .replace(/-+$/g, '');

  return description ? `${description}-${lot.lotNumber}` : String(lot.lotNumber);
};

/**
 * The lot's address, under the make and model it belongs to:
 * `bmw/330i/bmw-330i-2021-miami-north-fl-45170291` (spec 010, FR-004).
 *
 * The three segments are not equally authoritative. `marka` and `model` exist so
 * the breadcrumb above the car points at pages that EXIST — the retired
 * `/aukcje/lot/` form put a segment in the trail with nothing behind it — and the
 * trailing lot number remains the only part that identifies the car. A stale or
 * wrong parent therefore redirects to the canonical rather than 404ing.
 *
 * The model segment is the GROUP canonical where the index knows the spelling,
 * because that is the value `/aukcje/<marka>/<model>` resolves; a spelling the
 * index has never seen falls through as itself, which `resolveModelSlug` still
 * resolves (it matches spellings as well as canonicals).
 */
export const buildLotPath = (lot: TLotSlugInput): string => {
  const marka = toAuctionSlug(lot.make);
  const model = toAuctionSlug(groupForSpelling(lot.make, lot.model) ?? lot.model);
  const slug = buildLotSlug(lot);

  // Without a make or model there is no parent to nest under, so the retired
  // flat form stays the address of last resort rather than a broken path.
  if (!marka || !model) return `lot/${slug}`;

  return `${marka}/${model}/${slug}`;
};

/**
 * Reads the lot number back out. Accepts a bare number too, so a hand-typed
 * `/aukcje/lot/12598781` still resolves rather than 404ing.
 */
export const parseLotSlug = (slug: string): number | null => {
  const match = /(\d+)$/.exec(slug.trim());

  if (!match) return null;

  const lotNumber = Number(match[1]);

  return Number.isSafeInteger(lotNumber) && lotNumber > 0 ? lotNumber : null;
};

/**
 * Display casing. The catalogue is SHOUTING ("CHEVROLET", "MX-5 MIATA"), which
 * reads as shouting in a heading, so words are title-cased — except tokens that
 * are genuinely initialisms or contain digits ("BMW", "GMC", "F-350", "X5"),
 * which stay as the provider wrote them.
 */
export const toDisplayName = (value: string): string =>
  value
    .split(' ')
    .map(word => {
      if (word.length <= 3 || /\d/.test(word)) return word;

      return word.charAt(0) + word.slice(1).toLowerCase();
    })
    .join(' ');

/**
 * Display title for a lot: "2011 MAZDA 3".
 *
 * Lives here, with the other pure helpers, rather than in a normaliser. It is
 * used by `auction-lot-card.tsx`, a CLIENT component — and every normaliser
 * reaches the provider fetcher through the facility dictionary, which is
 * server-only and throws on module load in a browser. Importing it from there
 * would drag that whole chain into the client bundle.
 */
export const getLotTitle = (lot: { year: number | null; make: string | null; model: string | null }): string =>
  [lot.year, lot.make, lot.model].filter(Boolean).join(' ');
